/**
 * Token limits for a request, and whether the OCR text fits in it.
 *
 * The answer gets a fixed, generous limit; the request is held to an input
 * budget - the user's, within what the model's window leaves. The OCR text is
 * optional context: a model reads the receipt from the image and only
 * consults the text as a hint. So when the request would not fit the input
 * budget, the text is what goes - a request that overflows either fails
 * outright (Anthropic, OpenAI) or, worse, is silently cut from the front
 * (Ollama), which loses the system prompt first.
 *
 * Everything here is an estimate, and deliberately a pessimistic one. No
 * tokenizer ships with the app, and the cost of guessing low is a failed or
 * truncated request, while the cost of guessing high is only a missed hint.
 */

import type { CategoryOption } from '../categories.js';
import { RECEIPT_JSON_SCHEMA, receiptJsonSchema } from '../extraction.js';
import { buildUserPrompt, withCategoryInstructions, type CorrectionContext } from '../prompt.js';

/**
 * Characters per token, rounded down. English prose runs near four; Swedish,
 * digits, receipt abbreviations and JSON punctuation all tokenize worse.
 */
const CHARS_PER_TOKEN = 3;

/**
 * One receipt image, at the 1568 px long edge the app sends. Anthropic charges
 * about 1600 for that, OpenAI's high detail about 1100-1500, and local vision
 * models less; the largest is used for all of them.
 */
export const IMAGE_TOKEN_ESTIMATE = 1800;

/** Roles, message framing and the provider's own tool/schema wrapping. */
const FRAMING_TOKEN_ESTIMATE = 200;

/**
 * The answer's token limit, the same for every provider. A receipt's JSON
 * never comes near it; it is set high so that a long receipt, or a reasoning
 * model's thinking, is never cut off mid-answer.
 */
export const MAX_OUTPUT_TOKENS = 32_768;

/**
 * Models that reject an output limit as high as {@link MAX_OUTPUT_TOKENS} with
 * a 400. They get their own ceiling rather than a failed extraction.
 */
const OUTPUT_CEILINGS: [RegExp, number][] = [
  [/(^|\/)gpt-4o/i, 16_384],
  [/(^|\/)gpt-4(-turbo)?($|-\d)/i, 4096],
  [/claude-3-5/i, 8192],
  [/claude-3-/i, 4096],
];

/** The output limit to send for a model: {@link MAX_OUTPUT_TOKENS}, or less where the model allows less. */
export function outputTokensFor(model: string): number {
  return OUTPUT_CEILINGS.find(([pattern]) => pattern.test(model))?.[1] ?? MAX_OUTPUT_TOKENS;
}

/** Ollama's own default `num_ctx`, for a caller that sets none. */
const OLLAMA_DEFAULT_CONTEXT = 4096;

/** For a model this table does not know: small enough for most local servers behind an OpenAI-shaped API. */
const UNKNOWN_MODEL_CONTEXT = 32_768;

/** Known context windows, matched against the model name. First match wins; each is the smallest in its family. */
const KNOWN_CONTEXTS: [RegExp, number][] = [
  [/claude/i, 200_000],
  [/gemini/i, 1_000_000],
  [/(^|\/)(gpt-4\.1|gpt-5)/i, 400_000],
  [/(^|\/)(gpt-4o|gpt-4-turbo|o\d)/i, 128_000],
  [/(^|\/)(mistral|pixtral|qwen|llama-?[34]|grok)/i, 32_768],
];

export type BudgetProvider = 'anthropic' | 'openai' | 'ollama';

/**
 * The model's whole context window, input and output together. An explicit
 * `contextTokens` always wins - for Ollama it is the `num_ctx` the request
 * sets, which is the only window there is.
 */
export function contextWindowFor(provider: BudgetProvider, model: string, contextTokens?: number | null): number {
  if (contextTokens && contextTokens > 0) return contextTokens;
  if (provider === 'ollama') return OLLAMA_DEFAULT_CONTEXT;
  if (provider === 'anthropic') return 200_000;
  return KNOWN_CONTEXTS.find(([pattern]) => pattern.test(model))?.[1] ?? UNKNOWN_MODEL_CONTEXT;
}

/**
 * The room the input always leaves the answer: a long receipt's JSON with
 * categories. The answer is not reserved its whole limit up front - a 32k
 * window would then have nothing left for the request - but gets whatever the
 * request leaves, up to that limit (see {@link outputTokensWithin}).
 */
export const MIN_OUTPUT_TOKENS = 8192;

export interface TokenLimits {
  maxInputTokens?: number | null;
  maxOutputTokens: number;
  contextTokens?: number | null;
}

/**
 * How many tokens the request itself may take: the user's `maxInputTokens`,
 * and never more than the window leaves once the answer has its floor.
 */
export function inputBudgetFor(provider: BudgetProvider, model: string, limits: TokenLimits): number {
  const reserve = Math.min(limits.maxOutputTokens, MIN_OUTPUT_TOKENS);
  const window = contextWindowFor(provider, model, limits.contextTokens) - reserve;
  return limits.maxInputTokens && limits.maxInputTokens > 0 ? Math.min(limits.maxInputTokens, window) : window;
}

/**
 * The answer's limit to send with a request of `inputTokens` (from
 * {@link estimateInputTokens}): the model's own limit, within what the window
 * leaves after the request, and never below the floor the input budget kept.
 */
export function outputTokensWithin(provider: BudgetProvider, model: string, limits: TokenLimits, inputTokens: number): number {
  const left = contextWindowFor(provider, model, limits.contextTokens) - inputTokens;
  const floor = Math.min(limits.maxOutputTokens, MIN_OUTPUT_TOKENS);
  return Math.max(floor, Math.min(limits.maxOutputTokens, left));
}

/** A pessimistic token count for a piece of text. */
export function estimateTextTokens(text: string): number {
  return Math.ceil(text.length / CHARS_PER_TOKEN);
}

/** Estimated tokens for a request: prompts, schema and images. The answer is not counted. */
export function estimateInputTokens(
  system: string,
  userPrompt: string,
  images: number,
  schema: unknown = RECEIPT_JSON_SCHEMA,
): number {
  return (
    estimateTextTokens(system) +
    estimateTextTokens(userPrompt) +
    // Counted even under structured output: the provider feeds it to the model too.
    estimateTextTokens(JSON.stringify(schema)) +
    images * IMAGE_TOKEN_ESTIMATE +
    FRAMING_TOKEN_ESTIMATE
  );
}

export interface UserPromptBudget {
  /** The system prompt as sent. */
  system: string;
  correction?: CorrectionContext | null;
  ocrText?: string | null;
  images: number;
  /** From {@link inputBudgetFor}. */
  inputTokens: number;
}

/**
 * The user prompt, with the OCR text in it when the request still fits the
 * input budget and without it when it does not.
 */
export function userPromptWithinBudget(budget: UserPromptBudget): { userPrompt: string; ocrTextOmitted: boolean } {
  const withOcr = buildUserPrompt(budget.correction, budget.ocrText);
  if (!budget.ocrText?.trim()) return { userPrompt: withOcr, ocrTextOmitted: false };

  if (estimateInputTokens(budget.system, withOcr, budget.images) <= budget.inputTokens) {
    return { userPrompt: withOcr, ocrTextOmitted: false };
  }
  return { userPrompt: buildUserPrompt(budget.correction), ocrTextOmitted: true };
}

export interface ExtractionPromptBudget extends UserPromptBudget {
  /** The categories to offer the model, if any. */
  categories?: readonly CategoryOption[] | null;
}

export interface ExtractionPrompts {
  /** The system prompt as sent: with the category list when it fitted. */
  system: string;
  userPrompt: string;
  /** The schema to ask for: with `category` fields when the list fitted. */
  schema: Record<string, unknown>;
  ocrTextOmitted: boolean;
  /** True when categories were offered but the list did not fit, and was left out. */
  categoriesOmitted: boolean;
}

/**
 * The prompts for a reading request, with the OCR text and the category list
 * in it as far as the input budget allows.
 *
 * The OCR text goes in first: it helps the reading itself, and a category
 * list left out here can still be sent in a second, text-only call once the
 * lines are read (see `followUpCategories`), while the OCR text cannot. So
 * the list is the first thing dropped, and the OCR text only after it; with
 * the OCR text gone, the list is tried again on its own.
 */
export function extractionPromptsWithinBudget(budget: ExtractionPromptBudget): ExtractionPrompts {
  const offered = budget.categories && budget.categories.length > 0 ? budget.categories : null;
  const hasOcr = Boolean(budget.ocrText?.trim());

  const attempt = (categories: boolean, ocr: boolean): ExtractionPrompts => ({
    system: categories && offered ? withCategoryInstructions(budget.system, offered) : budget.system,
    userPrompt: buildUserPrompt(budget.correction, ocr ? budget.ocrText : null),
    schema: receiptJsonSchema(categories && offered !== null),
    ocrTextOmitted: hasOcr && !ocr,
    categoriesOmitted: offered !== null && !categories,
  });
  const fits = (prompts: ExtractionPrompts): boolean =>
    estimateInputTokens(prompts.system, prompts.userPrompt, budget.images, prompts.schema) <= budget.inputTokens;

  const candidates = [attempt(true, true), attempt(false, true), attempt(true, false), attempt(false, false)];
  return candidates.find(fits) ?? candidates[candidates.length - 1]!;
}
